import {
  ADMIN_ROLE,
  MEMBER_ROLE,
  UDS_SCHEMAS,
} from "@/src/constants/roles-permissions.constants";
import type {
  BlocksPermission,
  BlocksPermissionsResponse,
  Organization,
  SwitchOrgPayload,
  UdsPolicyGetApiResponse,
  UdsPolicyGetResponse,
  UdsPolicyMapEntry,
  UdsPolicyRule,
  UpdateRolePermissionsPayload,
} from "@/src/models/permissions.model";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import Toast from "react-native-toast-message";
import { BLOCKS_PERMISSION_QUERY_KEYS } from "../../query-keys/permissions.keys";
import apiClient from "../api/axios";
import { accountStore } from "@/src/services/api/account-store";
import {
  getRefreshToken,
  getRememberMe,
  invalidateIdentity,
  saveRefreshToken,
  saveToken,
  setActiveOrg,
  setOrgSelected,
} from "../api/auth.api";
import { Platform } from "react-native";

const PROJECT_KEY = process.env.EXPO_PUBLIC_X_BLOCKS_KEY;

const fetchBlocksPermissions = (role?: string) =>
  apiClient.post("/idp/v1/Iam/GetPermissions", {
    page: 0,
    pageSize: 0,
    query: "",
    ...(role ? { roles: [role] } : {}),
    projectKey: PROJECT_KEY,
  });

export const useAllBlocksPermissionsQuery = () =>
  useQuery<BlocksPermissionsResponse>({
    queryKey: BLOCKS_PERMISSION_QUERY_KEYS.allPermissions,
    queryFn: async () => (await fetchBlocksPermissions()).data,
  });

export const useRoleBlocksPermissionsQuery = (role: string) =>
  useQuery<BlocksPermissionsResponse>({
    queryKey: BLOCKS_PERMISSION_QUERY_KEYS.permissions(role),
    queryFn: async () => (await fetchBlocksPermissions(role)).data,
    enabled: !!role,
  });

const updateRolePermissions = (payload: UpdateRolePermissionsPayload) =>
  apiClient.post("/idp/v1/Iam/SetRoles", {
    addPermissions: payload.addPermissions,
    removePermissions: payload.removePermissions,
    projectKey: PROJECT_KEY,
    slug: payload.slug,
  });

export const useUpdateRolePermissionsMutation = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: updateRolePermissions,
    onSuccess: (_response, payload) => {
      queryClient.invalidateQueries({
        queryKey: BLOCKS_PERMISSION_QUERY_KEYS.permissions(payload.slug),
      });
      Toast.show({
        type: "success",
        text1: t("SUCCESS_ROLE_PERMISSIONS_UPDATED"),
      });
    },
    onError: () => {
      Toast.show({ type: "error", text1: t("ERROR_ROLE_PERMISSIONS_UPDATED") });
    },
  });
};

const createRole = (payload: {
  name: string;
  description?: string;
  slug: string;
}) =>
  apiClient.post("/idp/v1/Iam/CreateRole", {
    name: payload.name,
    description: payload.description ?? "",
    slug: payload.slug,
    projectKey: PROJECT_KEY,
  });

export const useCreateRoleMutation = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: createRole,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: BLOCKS_PERMISSION_QUERY_KEYS.allPermissions,
      });
      Toast.show({ type: "success", text1: t("SUCCESS_ROLE_CREATED") });
    },
    onError: (error: any) => {
      const serverError = error?.response?.data?.errors;
      const message =
        (serverError && typeof serverError === "object"
          ? Object.values(serverError)[0]
          : serverError) ?? t("ERROR_ROLE_CREATED");
      Toast.show({ type: "error", text1: message as string });
    },
  });
};

export const useSwitchOrgMutation = () => {
  return useMutation({
    mutationFn: async ({ orgId, refreshToken }: SwitchOrgPayload) => {
      const isWeb = Platform.OS === "web";
      const token = refreshToken || (await getRefreshToken()) || "";
      const rememberMe = await getRememberMe();
      const body = isWeb
        ? `grant_type=switch_organization&org_id=${encodeURIComponent(orgId)}&remember_me=${rememberMe}`
        : `refresh_token=${encodeURIComponent(token)}&grant_type=switch_organization&org_id=${encodeURIComponent(orgId)}&remember_me=${rememberMe}`;
      return apiClient
        .post("/idp/v1/Authentication/Token", body, {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          transformRequest: [
            (requestData, headers) => {
              headers["Content-Type"] = "application/x-www-form-urlencoded";
              return requestData;
            },
          ],
        })
        .then((res) => ({ ...res.data, orgId }));
    },
    onSuccess: async (data) => {
      if (Platform.OS === "web") {
        await setActiveOrg(data.orgId);
        await setOrgSelected(true);
        invalidateIdentity();
        return;
      }
      await saveToken(data.access_token);
      await saveRefreshToken(data.refresh_token);
      await setOrgSelected(true);
      const activeAccount = await accountStore.getActive();
      if (activeAccount) {
        await accountStore.upsert({
          ...activeAccount,
          accessToken: data.access_token,
          refreshToken: data.refresh_token,
        });
      }
    },
  });
};

export const useOrganizationsQuery = (options?: { enabled?: boolean }) =>
  useQuery<ReadonlyArray<Organization>>({
    queryKey: BLOCKS_PERMISSION_QUERY_KEYS.organizations,
    queryFn: async () => {
      const response = await apiClient.get("/idp/v1/Iam/GetOrganizations", {
        params: {
          projectKey: PROJECT_KEY,
          page: 0,
          pageSize: 100,
        },
      });
      return response?.data?.organizations;
    },
    enabled: options?.enabled ?? true,
  });

const isRoleEnabled = (
  rules: ReadonlyArray<UdsPolicyRule>,
  role: string,
): boolean => rules.some((rule) => rule.staticValue === role);

const fetchUdsPolicyMap = async (): Promise<
  Record<string, UdsPolicyMapEntry>
> => {
  const [iamResponse, ...udsResponses] = await Promise.all([
    apiClient.post("/idp/v1/Iam/GetPermissions", {
      page: 0,
      pageSize: 0,
      query: "",
      projectKey: PROJECT_KEY,
    }),
    ...UDS_SCHEMAS.map((schema) =>
      apiClient.get<UdsPolicyGetApiResponse>(
        `/uds/v1/data-access/policy/${schema}/get`,
        { params: { projectKey: PROJECT_KEY } },
      ),
    ),
  ]);

  const iamPermissions: ReadonlyArray<BlocksPermission> =
    iamResponse.data?.data ?? [];

  const udsPolicies: ReadonlyArray<UdsPolicyGetResponse> = udsResponses.flatMap(
    (res) => res.data?.data ?? [],
  );

  const udsByName = udsPolicies.reduce(
    (acc, policy) => ({ ...acc, [policy.policyName]: policy }),
    {} as Record<string, UdsPolicyGetResponse>,
  );

  return iamPermissions.reduce(
    (map, iamPermission) => {
      const udsPolicy = udsByName[iamPermission.name];
      if (!udsPolicy) return map;

      const rules = udsPolicy.ruleGroup.rules;

      return {
        ...map,
        [iamPermission.itemId]: {
          ...udsPolicy,
          adminEnabled: isRoleEnabled(rules, ADMIN_ROLE),
          memberEnabled: isRoleEnabled(rules, MEMBER_ROLE),
        } satisfies UdsPolicyMapEntry,
      };
    },
    {} as Record<string, UdsPolicyMapEntry>,
  );
};

export const useUdsPolicyMapQuery = () =>
  useQuery<Record<string, UdsPolicyMapEntry>>({
    queryKey: BLOCKS_PERMISSION_QUERY_KEYS.udsPolicyMap,
    queryFn: fetchUdsPolicyMap,
    staleTime: 5 * 60 * 1000,
  });

const reloadUdsConfigurations = (): Promise<void> =>
  apiClient.post(`/uds/v1/dbujnb/configurations/${PROJECT_KEY}/reload`, {});

export const useReloadUdsConfigurationsMutation = () =>
  useMutation({
    mutationFn: reloadUdsConfigurations,
  });
